import { EyeOff, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatCurrency } from "@/lib/format";

// INTERNAL sourcing intel — shown only inside the editor, never on PDFs/portal.
export const SourcingIntelPanel = ({ intel, summary }) => {
  if (!intel?.length) return null;
  return (
    <div data-testid="sourcing-intel-panel" className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
        <EyeOff size={15} />
        Sourcing Intel — INTERNAL ONLY (never shown to the customer)
      </div>
      {summary && <p className="text-xs text-amber-800">{summary}</p>}
      <div className="space-y-3">
        {intel.map((it, i) => (
          <div key={i} className="rounded-lg bg-white/70 border border-amber-200 p-3 text-xs space-y-1.5">
            <div className="font-semibold text-slate-800">{it.description}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
              {it.market_price_usd > 0 && <span>Intl market: <b>${Number(it.market_price_usd).toLocaleString()}</b></span>}
              {it.market_price_ugx > 0 && <span>UG market: <b>{formatCurrency(it.market_price_ugx, "UGX")}</b></span>}
              {it.best_cost_ugx > 0 && <span className="text-emerald-700">Best buy cost: <b>{formatCurrency(it.best_cost_ugx, "UGX")}</b></span>}
            </div>
            {(it.suppliers || []).length > 0 && (
              <ul className="space-y-1 pt-1">
                {it.suppliers.map((s, j) => (
                  <li key={j} className="flex items-start gap-1.5">
                    <ExternalLink size={11} className="mt-0.5 shrink-0 text-amber-600" />
                    <span>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noreferrer" className="font-medium text-blue-700 hover:underline">{s.name || s.url}</a>
                      ) : (
                        <span className="font-medium">{s.name}</span>
                      )}
                      {s.note ? <span className="text-slate-500"> — {s.note}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const quoteIssues = (form) => {
  const errors = [];
  if (!form.client_name?.trim()) errors.push("Client name is missing");
  if (!form.title?.trim()) errors.push("Subject / title is missing");
  if (!form.client_email?.trim()) errors.push("Client email is missing — needed to send for review");
  const items = form.items || [];
  if (!items.length) errors.push("No line items added");
  items.forEach((it, i) => {
    const n = i + 1;
    if (!it.description?.trim()) errors.push(`Item ${n}: description is empty`);
    if (!((parseFloat(it.qty) || 0) > 0)) errors.push(`Item ${n}: quantity must be greater than 0`);
    const price = parseFloat(it.unit_price) || 0;
    const cost = parseFloat(it.cost_price) || 0;
    if (price <= 0) errors.push(`Item ${n}: unit price is 0 — set a sale price`);
    else if (cost > 0 && price < cost) errors.push(`Item ${n}: selling BELOW cost (${Math.round(((price / cost) - 1) * 100)}% margin)`);
  });
  return errors;
};

export const QuoteIssues = ({ errors }) => (
  errors.length ? (
    <div data-testid="quote-issues-panel" className="rounded-xl border border-red-200 bg-red-50 p-4">
      <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
        <AlertTriangle size={15} /> Review before sending — {errors.length} issue{errors.length > 1 ? "s" : ""}
      </div>
      <ul className="text-xs text-red-700 space-y-1 list-disc pl-5">
        {errors.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </div>
  ) : (
    <div data-testid="quote-ready-banner" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-emerald-700 text-sm font-medium">
      <CheckCircle2 size={15} /> No issues found — quotation is ready to send for customer review.
    </div>
  )
);
